import { ChangeDetectionStrategy, Component, ViewChild, Injector } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { Pagination, PermissionSetService } from 'blg-akaun-ts-lib';
import * as moment from 'moment';
import { PermissionFacade } from '../../../../../../../modules/permission/facades/permission.facade';
import { PermSetListingSearchModel } from '../../../../../../../models/advanced-search-models/perm-set-listing-search.model';
import { PermissionStates } from '../../../../../permission-controller/root';
import { RolePermActions } from '../../../../../../../modules/permission/role-permissions-controller/actions';
import { RolePermSelectors } from '../../../../../../../modules/permission/role-permissions-controller/selectors';
import { RolePermStates } from '../../../../../../../modules/permission/role-permissions-controller/states';
import { gridOptionsServerSide, pageFiltering, pageSorting, serverSideClear } from '../../../../../../../listing.utils';
import { PaginationComponent } from '../../../../../../../utilities/pagination/pagination.component';
import { ViewColumnComponent } from '../../../../../../../view-column.component';
import { AppConfig } from '../../../../../../../visa';
import { SubSink } from 'subsink2';
import { SearchQueryModel } from '../../../../../../../models/query.model';
import { AgGridAngular } from 'ag-grid-angular';
import { ListingInputModel } from '../../../../../../../models/listing-input.model';
import { ListingService } from '../../../../../../../services/listing-service';
import { PaginationV2Component } from '../../../../../../../utilities/pagination-v2/pagination-v2.component';
import { SharedUtilitiesModule } from '../../../../../../../utilities/shared-utilities.module';
import { AdvancedSearchGeneralComponent } from '../../../../../../../utilities/advanced-search-general/advanced-search-general.component'
import { GridOptions } from "ag-grid-enterprise";
import { of, from, Observable, Subject } from "rxjs";
import { debounceTime, map, mergeMap, toArray } from "rxjs/operators";
import { ToastrService } from "ngx-toastr";
import { SharedUtilitiesHelperFunctions } from '../../../../../../../utilities/helper-functions';
import { TranslateService } from "@ngx-translate/core";

interface LocalState {
  deactivateReturn: boolean;
  selectedIndex: number;
  deactivateAdd: boolean;
  deactivateList: boolean;
}

@Component({
  selector: 'app-role-permission-set-add-listing',
  templateUrl: './role-permission-set-add-listing.component.html',
  styleUrls: ['./role-permission-set-add-listing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})
export class RolePermissionSetAddListingComponent extends ViewColumnComponent {

  @ViewChild('agGrid', {static: false}) aggrid!: AgGridAngular;

  protected readonly index = 5;
  private localState: LocalState;

  // initialise local states
  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateReturn$ = this.componentStore.select(state => state.localState.deactivateReturn);
  readonly selectedIndex$ = this.componentStore.select(state => (state.localState.selectedIndex));
  readonly deactivateAdd$ = this.componentStore.select(state => state.localState.deactivateAdd);
  readonly deactivateList$ = this.componentStore.select(state => (state.localState.deactivateList));

  // previous index
  prevIndex: number;
  private prevLocalState: any;

  // api visa
  apiVisa = AppConfig.apiVisa;

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subs = new SubSink;

  formDisabled: boolean = true;

  searchModel = PermSetListingSearchModel;

  // initialise ag grid
  gridApi;

  // perm sets that are already added
  addedEntities = new Map<string, any>();
  // currently selected entities
  selectedEntities = new Map<string, any>();

  // this applet guid
  appletGuid: string = sessionStorage.getItem('appletGuid');
  // this role guid
  roleGuid: string;
  gridOptions: GridOptions = {
    statusBar: {
      statusPanels: [
        { statusPanel: 'agTotalAndFilteredRowCountComponent', key: 'totalAndFilter', align: 'left' },
        { statusPanel: 'agSelectedRowCountComponent', align: 'left' },
        { statusPanel: 'agAggregationComponent', align: 'right' },
      ]
    },
    defaultColDef: {
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      width: 100,
      sortable: true,
      resizable: true,
      suppressSizeToFit: false,
    },
    columnTypes: SharedUtilitiesHelperFunctions.columnTypes,
    suppressAggFuncInHeader: true,
    pagination: true,
    animateRows: true,
    sideBar: true,
    rowSelection: 'multiple',
    suppressRowClickSelection: false,
    multiSortKey: 'ctrl',
    onSortChanged: this.onSortChanged,
    onFilterChanged: this.onFilterChanged,
    isRowSelectable: params => {
      return !params.data.isLocked;
    },
  }



  @ViewChild(PaginationV2Component, { static: false })
  private paginationComponent: PaginationV2Component;

  selectedGuid: string;
  compGuid;
  branchLinks: any[] =[];
  totalRecords = 0;
  limit = 10000;
  selectedIndex;
  rowData = [];
  rowDataPermLink = [];
  searchQuery: SearchQueryModel;
  columnsDefs;

  //translation variable
  title: string;
  back_button: string;
  add_button: string;
  code_text: string;
  name_text: string;
  created_date_text: string;
  updated_date_text: string;

  constructor(
    private toastr: ToastrService,
    private listingService: ListingService,
    private permissionSetService: PermissionSetService,
    private viewColFacade: PermissionFacade,
    private store: Store<PermissionStates>,
    private componentStore: ComponentStore<{localState: LocalState}>,
    private injector: Injector
  ) {
    super();
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.title = res.add_permission_set;
        this.back_button = res.back_button;
        this.add_button = res.add_button_caps;
        this.code_text = res.code;
        this.name_text = res.name;
        this.created_date_text = res.created_date;
        this.updated_date_text = res.updated_date;
      });
    } else {
      // Fallback for applets that don't implement translation
      this.title = 'Add Permission Set';
      this.back_button = 'Back';
      this.add_button = 'ADD';
      this.code_text = 'Code';
      this.name_text = 'Name';
      this.created_date_text = 'Created Date';
      this.updated_date_text = 'Updated Date';
    }
  }

  /**
   * Lifecycle method: Called when the component is first initialised.
   */
  ngOnInit(): void {
    this.checkboxCellRenderer = this.checkboxCellRenderer.bind(this);
    this.subs.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState({localState: a});
    });
    this.subs.sink = this.selectedIndex$.subscribe(selectedIndex=>{
      this.selectedIndex=selectedIndex;
    })
    this.subs.sink = this.viewColFacade.prevIndex$.subscribe(resolve => this.prevIndex = resolve);
    this.subs.sink = this.viewColFacade.prevLocalState$().subscribe(resolve => this.prevLocalState = resolve);
    this.subs.sink = this.store.select(RolePermSelectors.selectGuid)
    .subscribe(x => {
      this.roleGuid = x;
    })
    this.columnsDefs = [
      {
        floatingFilter: false,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        maxWidth: 30,
        suppressColumnsToolPanel: true,
        sortable: false,
        resizable: true,
        suppressMenu: true,
      },
      {
        headerName: this.code_text,
        field: "code",
        type: 'textColumn'
      },
      {
        headerName: this.name_text,
        field: "name",
        type: 'textColumn'
      },
      {
        headerName: this.created_date_text,
        field: "created_date",
        type: 'dateColumn'
      },
      {
        headerName: this.updated_date_text,
        field: "updated_date",
        type: 'dateColumn'
      }
    ]
  }

  checkboxCellRenderer(params) {
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.checked = params.node.isSelected(); // Sync checkbox with row selection state

    // Disable checkbox if isLocked is true
    if (params.data && params.data.isLocked) {
      input.disabled = true;
    }

    // Handle checkbox change
    input.addEventListener('change', function () {
      if (!params.data.isLocked) {
        params.node.setSelected(input.checked); // Update AG Grid's selection state
      }
    });

    return input;
  }



  onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();
    this.getPermSetsInRole();
    this.getTotalData();
    this.createData();
  }

  autoSelectRows(permissionSets) {
    // Iterate over the row data and mark rows with matching guid as locked
    this.rowData = this.rowData.map(rowData => {
      const matchingPermSet = permissionSets.find(ps => ps.guid === rowData.guid);

      if (matchingPermSet) {
        return { ...rowData, isLocked: true };  // Mark row as locked if guid matches
      } else {
        return { ...rowData, isLocked: false };
      }
    });

    console.log(this.rowData)
    if (this.gridApi) {
      this.gridApi.setRowData(this.rowData);
    }

  }

  createData() {
    const formData = this.getInputModel();
    this.subs.sink = this.listingService.get("permission-set-hdr/backoffice-ep", formData, this.apiVisa).pipe(
      mergeMap(a => from(a.data).pipe(
        map(b => {
          return b;
        }),
        toArray(),
        map(c => {
          a.data = c;
          return a;
        })
      ))
    ).subscribe(resolved => {
      this.rowData = [...this.rowData, ...resolved.data];
      this.gridApi.setRowData(this.rowData);
      this.gridApi.paginationGoToLastPage();
      // this.paginationComponent.totalRecords.next(this.totalRecords);
      this.autoSelectRows(this.rowDataPermLink);
    }, err => {
      console.error(err);
    });
  };

  getInputModel() {
    if(!this.appletGuid){ return null; }
    console.log(this.searchQuery)
    const inputModel = {} as ListingInputModel;
    inputModel.search = (this.searchQuery?.keyword);
    inputModel.searchColumns = ['code','name'];
    inputModel.status = ['ACTIVE'];
    inputModel.orderBy = 'updated_date';
    inputModel.order = 'desc';
    inputModel.limit = this.limit;
    inputModel.offset = this.rowData.length;
    inputModel.calcTotalRecords = false;
    inputModel.showCreatedBy = false;
    inputModel.showUpdatedBy = false;
    inputModel.filterLogical = 'AND';
    inputModel.filterConditions = [];
    inputModel.childs = [];
    inputModel.joins = []
    inputModel.childs = [];

    inputModel.filterConditions.push(
      {
          "filterColumn": "applet_guid",
          "filterValues": [
            this.appletGuid
          ],
          "filterOperator": "="
      }
    );

    if(this.searchQuery){
      const code = SharedUtilitiesHelperFunctions.checkNull(this.searchQuery.queryString['code'], "");
      if(code){
        inputModel.filterConditions.push({
          "filterColumn": "code",
          "filterValues": [
            code
          ],
          "filterOperator": "IN"
        })
      }
      const name = SharedUtilitiesHelperFunctions.checkNull(this.searchQuery.queryString['name'], "");
      if(name){
        inputModel.filterConditions.push({
          "filterColumn": "name",
          "filterValues": [
            name
          ],
          "filterOperator": "IN"
        })
      }
    }


    return inputModel;
  }

  getTotalData() {
    const formData = this.getInputModel();
    formData.childs = [];
    formData.joins = [];

    formData.calcTotalRecords = true;
    formData.calcTotalRecordsOnly = true;
    this.subs.sink = this.listingService.get("permission-set-hdr/backoffice-ep", formData, this.apiVisa).pipe(
    ).subscribe(resolved => {
      this.totalRecords = resolved.totalRecords;
      // this.paginationComponent.totalRecords.next(this.totalRecords);
    }, err => {
      console.error(err);
    });
  };

  getPermSetsInRole() {
    const formData = this.getInputModelPermSetsInRole();
    formData.calcTotalRecords = true;
    this.listingService.get("permission-set-hdr/backoffice-ep", formData, this.apiVisa).pipe(
    ).subscribe(resolved => {
      this.rowDataPermLink = resolved.data;
    }, err => {
      console.error(err);
    });
  };

  getInputModelPermSetsInRole() {
    if(!this.appletGuid){ return null; }
    console.log(this.searchQuery)
    const inputModel = {} as ListingInputModel;
    inputModel.search = (this.searchQuery?.keyword);
    inputModel.searchColumns = ['code','name'];
    inputModel.status = ['ACTIVE'];
    inputModel.orderBy = 'updated_date';
    inputModel.order = 'desc';
    inputModel.limit = 10000;
    inputModel.offset = this.rowData.length;
    inputModel.calcTotalRecords = false;
    inputModel.showCreatedBy = false;
    inputModel.showUpdatedBy = false;
    inputModel.filterLogical = 'AND';
    inputModel.filterConditions = [];
    inputModel.childs = [];
    inputModel.joins = []

    inputModel.filterConditions.push(
      {
          "filterColumn": "applet_guid",
          "filterValues": [
            this.appletGuid
          ],
          "filterOperator": "="
      }
    );
    inputModel.childs = [
      {
        "tableName": "app_perm_assignee_target_link",
        "joinColumn": "perm_set_hdr_guid",
        "joinType": "inner join",
        "filterLogical": "AND",
        "filterConditions": [{
          "filterColumn": "assignee_hdr_guid",
          "filterValues": [this.roleGuid],
          "filterOperator": "IN"
        }]
      },
    ];


    return inputModel;
  }

  onSearch(e: SearchQueryModel) {
    console.log("search", e);
    if (!e.isEmpty) {
      if (e.keyword && e.keyword.length > 0 && e.keyword.length < 3) {
        this.toastr.error(
          'Search keyword must more than 2 characters.',
          'Keyword',
          {
            tapToDismiss: true,
            progressBar: true,
            timeOut: 1300
          }
        );
        return;
      }

      this.searchQuery = e;
    } else {
      this.searchQuery = null;
    }
    this.clear();
    this.getPermSetsInRole();
    this.getTotalData();
    this.createData();
  }

  clear() {
    this.gridApi.setRowData(null);
    this.totalRecords = 0;
    this.rowData = [];
    this.rowDataPermLink = [];
    // this.paginationComponent.totalRecords.next(this.totalRecords);
  }


  /**
   * Add a permission set to the role.
   */
  onAdd() {
    const selectedRows = this.gridApi.getSelectedRows();

    const permSetHdrGuids = selectedRows.map(row => row.guid);
    const filteredPermSetHdrGuids = permSetHdrGuids.filter(guid => !this.rowDataPermLink.includes(guid));
    console.log('filteredPermSetHdrGuids',filteredPermSetHdrGuids)
    // clear selected
    this.selectedEntities.clear();
    this.aggrid.api.deselectAll();
    // dispatch ngrx action to add permission sets to role
    //this.store.dispatch(RolePermActions.addPermSetsToRoleInit({permSetLines: permSetLines, appletGuid: this.appletGuid, roleGuid: this.roleGuid}));
    this.store.dispatch(RolePermActions.addPermSetHdrToRoleInit({permSetHdrGuids: permSetHdrGuids, appletGuid: this.appletGuid, roleGuid: this.roleGuid,prevIndex: this.prevIndex, prevLocalState: this.prevLocalState}));
    // set form to disable again
    //this.formDisabled = true;
  };
  /**
   * Return to the previous drawer/view column.
   */
   onReturn() {
     // activate return, add and list in the previous component
    this.viewColFacade.updateInstance(this.prevIndex, {
      ...this.prevLocalState,
      deactivateReturn: false,
      deactivateAdd: false,
      deactivateList: false
    });
    this.viewColFacade.onPrev(this.prevIndex);
  }

  onFirstDataRendered(params) {
    params.columnApi.autoSizeAllColumns();
  }

  onSortChanged(e) {
    e.api.refreshCells();
  }

  onFilterChanged(e) {
    e.api.refreshCells();
  }

  onMorePage() {
    if (this.rowData.length < this.totalRecords) {
      this.createData();
    }
  }
  /**
   * Lifecycle method: Called when component is destroyed.
   */
   ngOnDestroy() {
    this.subs.unsubscribe();
  }

}

